import React from "react";
import {
  DataTableV2DateTypes,
  IDataTableV2DateState,
} from "../preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { getDataTablev2InitialDate } from "../helpers/dataTableV2DatesFilter";
import { IDataTableV2Tab } from "../components/TableTabs/DataTableV2Tabs.types";
import {
  IDataTableV2SearchFilterSearchItem,
  IDatatableV2AdvancedSearchFilter,
} from "../preDefinedPlugins/SearchFilter/SearchFilter.types";
import produce from "immer";
import { IDataTableV2ColumnsVisibilityState } from "../preDefinedPlugins/DataTableV2ColumnsVisibility/DataTableV2ColumnsVisibility.types";
import { useDataTableV2Context } from "../context/DataTableV2Provider";
import { IUserFilterConfig } from "./useTableV2State.types";

export interface IDataInitialTableV2State {
  filtersInitialState?: {
    defaultDateRange?: DataTableV2DateTypes;
    selectedTab?: any;
    search?: IDataTableV2SearchFilterSearchItem[];
  };
  columnsConfig?: {
    columnVisibility?: IDataTableV2ColumnsVisibilityState;
  };
  pagination?: {
    pageNumber: number;
    rowsInPerPage: number;
    apiFetchRowCount: number;
    extraFetchFactor: number;
  };
  cachingFilters?: {
    uniqueKey: string;
  };
  masterColumnResize?: {
    [key: string]: {
      width: number;
    };
  };
  otherCustomStateProperties?: {
    [key: string]: any;
  };
}

const getDataFromContext = (
  initialState: IDataInitialTableV2State,
  getCachedDataByKey: (uniqueKey: string) => IUserFilterConfig | null
): IDataTableV2State => {
  const {
    filtersInitialState = {
      defaultDateRange: "last30Days",
      selectedTab: "0",
      search: [],
    },
    pagination = {
      pageNumber: 1,
      rowsInPerPage: 25,
      apiFetchRowCount: 0,
      extraFetchFactor: 0,
    },
    columnsConfig = {
      columnVisibility: {},
    },
    cachingFilters,
    masterColumnResize = {},
    otherCustomStateProperties = {},
  } = initialState;
  if (initialState.cachingFilters && initialState.cachingFilters.uniqueKey) {
    const data = getCachedDataByKey(initialState.cachingFilters.uniqueKey);
    if (data) {
      const newData = {
        ...data,
        dateState: {
          rangeType: data.dateState.rangeType || filtersInitialState.defaultDateRange || "last30Days",
          filterBy: data.dateState.filterBy ?? "insert_ts",
          dates: data.dateState.rangeType === "custom" ? data.dateState.dates : {
            ...data.dateState.dates,
            ...getDataTablev2InitialDate(
              data.dateState.rangeType || "last30Days"
            ),
          },
        },
        pagination: {
          ...data.pagination,
          pageNumber: pagination.pageNumber,
          apiFetchRowCount: pagination.apiFetchRowCount,
          extraFetchFactor: pagination.extraFetchFactor,
          rowsInPerPage: pagination.rowsInPerPage,
        },
      };

      return newData;
    }
  }
  return {
    dateState: {
      rangeType: filtersInitialState.defaultDateRange || "last30Days",
      filterBy: "insert_ts",
      dates: {
        ...getDataTablev2InitialDate(
          filtersInitialState.defaultDateRange || "last30Days"
        ),
      },
    },
    tabs: {
      selectedTab: filtersInitialState.selectedTab,
      tabs: [] as IDataTableV2Tab[],
    },
    searchState:
      filtersInitialState.search || ([] as IDatatableV2AdvancedSearchFilter),
    columnsConfig: {
      columnVisibility: columnsConfig.columnVisibility || {},
    },
    pagination: pagination,
    masterColumnResize: masterColumnResize,
    otherCustomStateProperties: otherCustomStateProperties,
  };
};

export interface IDataTableV2State {
  dateState: {
    rangeType: DataTableV2DateTypes;
    filterBy?: "insert_ts" | "create_ts";
    dates: {
      fromDate: string;
      toDate: string;
    };
  };
  tabs: {
    selectedTab: any;
    tabs: IDataTableV2Tab[];
  };
  searchState: IDataTableV2SearchFilterSearchItem[];
  columnsConfig: {
    columnVisibility: {};
  };
  pagination: {
    pageNumber: number;
    rowsInPerPage: number;
    apiFetchRowCount: any;
    extraFetchFactor: number;
  };
  masterColumnResize: {
    [key: string]: {
      width: number;
    };
  };
  otherCustomStateProperties: {
    [key: string]: any;
  };
}

export const useTableV2State = (initialData: IDataInitialTableV2State) => {
  const { getCachedDataByKey, saveDataInCache, cachingDataState, isCacheReady } =
    useDataTableV2Context();

  const uniqueKeyInit = initialData.cachingFilters?.uniqueKey;
  // Did cached data already exist for this table at mount (e.g. localStorage
  // seeded synchronously on a full page reload)?
  const hadCacheAtMount = Boolean(
    uniqueKeyInit && getCachedDataByKey(uniqueKeyInit)
  );

  const initialRef = React.useRef(
    getDataFromContext(initialData, getCachedDataByKey)
  );

  // True once the user changes any filter/state locally. Prevents late-arriving
  // server cache from clobbering the user's active selections.
  const hasUserInteractedRef = React.useRef(false);
  // Whether the table has cache to persist against. If cache was present at
  // mount (localStorage seeded on full reload), persistence is allowed
  // immediately. Otherwise it's flipped true once server hydration applies.
  const hasHydratedFromServerRef = React.useRef(hadCacheAtMount);
  // Guards the server-hydration effect so it applies at most once.
  const serverHydrationAppliedRef = React.useRef(false);

  // start column
  // Store the true initial column visibility (not from cache) for reset functionality
  const trueInitialColumnVisibility = React.useRef(
    initialData.columnsConfig?.columnVisibility || {}
  );
  // end column

  const [state, setState] = React.useState(initialRef.current);

  const updateStateInCache = (newSate: any, isUserAction: boolean = true) => {
    // Genuine user changes lock out server re-hydration so it can't overwrite
    // active selections. Programmatic updates (e.g. injecting tab list from
    // record counts) pass isUserAction=false so they don't block hydration.
    if (isUserAction) {
      hasUserInteractedRef.current = true;
    }

    // CRITICAL: do NOT persist anything until the saved config has been
    // resolved (server response on login, or localStorage on full reload).
    // On login the GET /user/get-user-meta-config is async and can be slow;
    // the table mounts with default state and fires programmatic updates
    // (e.g. setTableTabs from record counts). Persisting before the config is
    // ready would overwrite the user's saved filters with defaults.
    if (!isCacheReady) return;

    // After cache is ready, still skip pre-hydration programmatic updates so
    // they don't clobber not-yet-applied server data.
    const shouldPersist =
      isUserAction ||
      hasUserInteractedRef.current ||
      hasHydratedFromServerRef.current;
    if (!shouldPersist) return;

    if (initialData.cachingFilters && initialData.cachingFilters.uniqueKey) {
      saveDataInCache(initialData.cachingFilters.uniqueKey, newSate);
    }
  };

  // Re-hydrate from server-loaded cache. The table reads its initial state at
  // mount (into initialRef), but the server config (get-user-meta-config)
  // arrives later. When it lands in the context for this table's uniqueKey and
  // the user hasn't interacted yet, apply it once.
  const uniqueKey = uniqueKeyInit;
  React.useEffect(() => {
    if (!uniqueKey) return;
    if (hasUserInteractedRef.current || serverHydrationAppliedRef.current) return;
    // Already had cache at mount → initialRef already reflects it, nothing to do.
    if (hadCacheAtMount) return;

    const cachedForKey = cachingDataState?.[uniqueKey];
    if (!cachedForKey) return;

    const hydrated = getDataFromContext(initialData, (key) =>
      key === uniqueKey ? cachedForKey : null
    );

    setState((prev) => {
      // Preserve the live tab LIST (which is injected from record counts and
      // may be richer than what was saved), but apply the saved selectedTab.
      const merged: IDataTableV2State = {
        ...hydrated,
        tabs: {
          tabs:
            prev.tabs?.tabs && prev.tabs.tabs.length > 0
              ? prev.tabs.tabs
              : hydrated.tabs.tabs,
          selectedTab: hydrated.tabs.selectedTab,
        },
      };
      serverHydrationAppliedRef.current = true;
      hasHydratedFromServerRef.current = true;
      if (JSON.stringify(merged) === JSON.stringify(prev)) {
        return prev;
      }
      initialRef.current = merged;
      return merged;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cachingDataState, uniqueKey]);

  const setDateState = (newData: IDataTableV2DateState) => {
    //@ts-ignore
    const newState = produce(state, (draftState) => {
      draftState.dateState = newData;
      draftState.pagination.pageNumber = 1;
    });
    updateStateInCache(newState);
    setState(newState);
  };

  const setSelectedTab = (newData: any) => {
    const { pagination } = initialRef.current;
    //@ts-ignore
    const newState = produce(state, (draftState) => {
      draftState.tabs.selectedTab = newData;
      draftState.pagination.pageNumber = 1;
    });
    updateStateInCache(newState);
    setState(newState);
  };

  const setTableTabs = (newData: IDataTableV2Tab[]) => {
    if (JSON.stringify(state.tabs?.tabs) === JSON.stringify(newData)) {
      return;
    }
    //@ts-ignore
    const newState = produce(state, (draftState) => {
      draftState.tabs.tabs = newData;
    });
    // Programmatic (record-count driven) tab-list injection — not a user action,
    // so it must not block server re-hydration of saved filters.
    updateStateInCache(newState, false);
    setState(newState);
  };

  const setSearchState = (newData: IDatatableV2AdvancedSearchFilter) => {
    //@ts-ignore
    const newState = produce(state, (draftState) => {
      draftState.searchState = newData;
      draftState.pagination.pageNumber = 1;
    });
    updateStateInCache(newState);
    setState(newState);
  };

  const setColumnVisibility = (newData: IDataTableV2ColumnsVisibilityState) => {
    //@ts-ignore
    const newState = produce(state, (draftState) => {
      draftState.columnsConfig.columnVisibility = newData;
    });
    updateStateInCache(newState);
    setState(newState);
  };

  const onPageChange = (newPageNumber: number) => {
    //@ts-ignore
    const newState = produce(state, (draftState) => {
      draftState.pagination.pageNumber = newPageNumber;
      draftState.pagination.apiFetchRowCount =
        state.pagination.rowsInPerPage + state.pagination.extraFetchFactor;
    });
    updateStateInCache(newState);
    setState(newState);
  };

  const onRowsPerPageChange = (newPageNumber: number, rowsPerPage: number) => {
    //@ts-ignore
    const newState = produce(state, (draftState) => {
      draftState.pagination.pageNumber = newPageNumber;
      draftState.pagination.rowsInPerPage = rowsPerPage;
      draftState.pagination.apiFetchRowCount =
        rowsPerPage + state.pagination.extraFetchFactor;
    });
    updateStateInCache(newState);
    setState(newState);
  };

  const resetPaginationToInitialPagination = () => {
    const currentPagination = state.pagination;
    const { pagination } = initialRef.current;
    const isEqual =
      JSON.stringify(currentPagination) === JSON.stringify(pagination);
    if (isEqual) {
      return false;
    }

    //@ts-ignore
    const newState = produce(state, (draftState) => {
      draftState.pagination = pagination;
    });
    updateStateInCache(newState);
    setState(newState);
    return true;
  };

  const resizeCacheTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Always holds the latest state so the debounced resize save doesn't persist
  // a stale snapshot (which would clobber other fields changed meanwhile).
  const latestStateRef = React.useRef(state);
  React.useEffect(() => {
    latestStateRef.current = state;
  }, [state]);

  const onResizeMasterColumns = (newResizeValues: {
    [key: string]: { width: number };
  }) => {
    // Update React state immediately for visual sync
    setState((prev) => ({ ...prev, masterColumnResize: newResizeValues }));

    // Debounce localStorage/server write — only persist after 300ms of inactivity
    if (resizeCacheTimerRef.current) {
      clearTimeout(resizeCacheTimerRef.current);
    }
    resizeCacheTimerRef.current = setTimeout(() => {
      // Build from the LATEST state (not the stale closure) so other fields
      // changed since aren't reverted.
      const currentState = {
        ...latestStateRef.current,
        masterColumnResize: newResizeValues,
      };
      updateStateInCache(currentState);
    }, 300);
  };

  const setOtherCustomStateProperties = (key: string, value: any) => {
    const newState = produce(state, (draftState) => {
      draftState.otherCustomStateProperties[key] = value;
      draftState.pagination.pageNumber = 1;
    });

    updateStateInCache(newState);
    setState(newState);
  };

  const onMobileStateChange = (newState: IDataTableV2State) => {
    updateStateInCache(newState);
    setState(newState);
  };

  const resetState = () => {
    const data = {
      dateState: {
        rangeType:
          initialData.filtersInitialState?.defaultDateRange || "last30Days",
        filterBy: "insert_ts",
        dates: {
          ...getDataTablev2InitialDate(
            initialData.filtersInitialState?.defaultDateRange || "last30Days"
          ),
        },
      },
      tabs: { ...state.tabs },
      searchState:
        initialData.filtersInitialState?.search ||
        ([] as IDatatableV2AdvancedSearchFilter),
      // columnsConfig: {},
      // start column
      columnsConfig: {
        columnVisibility: trueInitialColumnVisibility.current,
      },
      // end column
      pagination: initialData.pagination,
      masterColumnResize: initialData.masterColumnResize,
      otherCustomStateProperties: initialData.otherCustomStateProperties,
    } as any;
    updateStateInCache(data);
    setState(data);
  };

  return {
    state,
    setState,
    resetState,
    setDateState,

    setTableTabs,

    setSelectedTab,

    setSearchState,
    setColumnVisibility,
    onPageChange,
    onRowsPerPageChange,
    onResizeMasterColumns,
    resetPaginationToInitialPagination,
    onMobileStateChange,
    setOtherCustomStateProperties,
    // start column
    initialColumnVisibility: trueInitialColumnVisibility.current,
    // end column
  };
};
